import { useState, useRef, useCallback, useEffect } from 'react';
import { usePresence } from '../site/usePresence';
import { authFetch } from '../auth';
import './AdminApp.css';

/* 홈페이지 원본 스타일 (페이지 복제본 렌더링용) */
import '../styles/base.css';
import '../styles/components.css';
import '../styles/responsive.css';
import '../site/siteEditor.css';

/* 계정 관리 / 활동 로그 / 문의·교육 신청 접수 내역 */
import AccountManager from '../account/AccountManager';
import LogViewer from '../account/LogViewer';
import InquiryLogs from '../account/InquiryLogs';
import EduApplicationLogs from '../account/EduApplicationLogs';

/* 페이지 복제본 (홈페이지와 동일한 화면 + 인라인 편집) */
import Home from '../site/pages/Home';
import Solutions from '../site/pages/Solutions';
import Applications from '../site/pages/Applications';
import Education from '../site/pages/Education';
import About from '../site/pages/About';
import Partners from '../site/pages/Partners';
import History from '../site/pages/History';
import Contact from '../site/pages/Contact';
import Inquiry from '../site/pages/Inquiry';
import Footer from '../site/pages/Footer';
import Privacy from '../site/pages/Privacy';
import ProductCategory from '../site/pages/ProductCategory';
import NetworkAplience from '../site/pages/products/networkAplience';
import RuggedProduct from '../site/pages/products/ruggedProduct';
import Industrial from '../site/pages/products/industrial';
import Ipc from '../site/pages/products/ipc';
import Military from '../site/pages/products/military';
import Railway from '../site/pages/products/railway';
import Modeling from '../site/pages/products/modeling';
import Hpc from '../site/pages/products/hpc';
import Laboratory from '../site/pages/products/laboratory';
import { CATEGORY_PRODUCTS } from '../site/seed';

// 솔루션 slug → 전용 제품 편집기 매핑.
// 여기 없는 slug는 기존 ProductCategory(범용)로 열림.
const PROD_EDITORS = {
  na: NetworkAplience,
  rugged: RuggedProduct,
  ipc: Ipc,
  industrial: Industrial,
  military: Military,
  railway: Railway,
  sensor: Modeling,
  hpc: Hpc,
  laboratory: Laboratory,
};

// 개발: localhost/IP → :5173 포트 사용
// 운영: admin.도메인 → 도메인만 남기고 프로토콜/포트 유지 (예: https://acetec-korea.co.kr)
const HOMEPAGE_BASE = (window.location.hostname === 'localhost' || /^[\d.]+$/.test(window.location.hostname))
  ? `http://${window.location.hostname}:5173`
  : `${window.location.protocol}//${window.location.hostname.replace(/^admin\./, '')}`;

const PAGES = [
  { id: 'home', label: '메인', path: '/', Comp: Home },
  { id: 'solutions', label: '응용 분야', path: '/solutions', Comp: Solutions },
  { id: 'applications', label: '적용 분야', path: '/applications', Comp: Applications },
  { id: 'education', label: '교육 신청', path: '/education', Comp: Education },
  { id: 'about', label: '회사 소개', path: '/about', Comp: About },
  { id: 'partners', label: '파트너사', path: '/partners', Comp: Partners },
  { id: 'history', label: '연혁', path: '/history', Comp: History },
  { id: 'contact', label: '오시는 길', path: '/contact', Comp: Contact },
  { id: 'inquiry', label: '문의하기', path: '/inquiry', Comp: Inquiry },
  { id: 'footer', label: '푸터', path: '/', Comp: Footer },
  { id: 'privacy', label: '개인정보처리방침', path: '/', Comp: Privacy },
];

const PERM_LABELS = { 1: '통합관리자', 5: '컨텐츠 관리자', 9: '계정 정지' };

export default function AdminApp({ account, onLogout }) {
  // 권한 1(통합관리자)만 계정 관리 접근 가능
  const canManageAccounts = !!account && account.intPermission === 1;

  // view: 'accounts' 또는 페이지 id — 새로고침 후에도 유지
  const [view, setView] = useState(() => {
    const saved = sessionStorage.getItem('admin-view');
    const defaultView = canManageAccounts ? 'accounts' : 'home';
    // 저장된 뷰가 없거나, 계정관리 권한이 없는데 accounts/logs 뷰면 기본값으로
    if (!saved) return defaultView;
    if (!canManageAccounts && (saved === 'accounts' || saved === 'logs')) return 'home';
    const validIds = new Set(['accounts', 'logs', 'inquiries', 'edu-apps', ...PAGES.map(p => p.id)]);
    if (!validIds.has(saved) && !saved.startsWith('product:')) return defaultView;
    return saved;
  });
  const [toast, setToast] = useState('');
  const [pwModal, setPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const isAccounts = canManageAccounts && view === 'accounts';
  const isLogs = canManageAccounts && view === 'logs';
  const isInquiries = view === 'inquiries';
  const isEduApps = view === 'edu-apps';
  const isProduct = view.startsWith('product:');
  const productSlug = isProduct ? view.slice('product:'.length) : null;
  const isViewer = isAccounts || isLogs || isInquiries || isEduApps; // 읽기 전용(편집·저장 없음)
  const activePage = !isViewer && !isProduct ? PAGES.find((p) => p.id === view) : null;
  const others = usePresence(isViewer ? null : view);

  const openProduct = (slug) => requestNavigate(() => setView(`product:${slug}`));

  // 페이지가 자신의 저장 함수를 등록 → 상단 "저장"이 호출 (없으면 UI 데모)
  const mainContentRef = useRef(null); // 저장 후 스크롤 위치 복원용
  const saveRef = useRef(null);
  const dirtyRef = useRef(null); // 현재 페이지의 미저장 변경 여부를 알려주는 함수
  const [saving, setSaving] = useState(false);
  // 저장 후 페이지를 리마운트해 DB를 다시 읽어오게 하는 키(인라인 편집 화면 갱신용)
  const [reloadKey, setReloadKey] = useState(0);
  const registerSave = useCallback((fn) => { saveRef.current = fn; }, []);
  const registerDirty = useCallback((fn) => { dirtyRef.current = fn; }, []);

  // 뷰 변경 시 sessionStorage에 저장 → 새로고침 후 복원
  useEffect(() => { sessionStorage.setItem('admin-view', view); }, [view]);

  // 저장 버튼 색상용: 400ms 폴링으로 dirty 여부를 반응형 state에 동기화
  const [isDirty, setIsDirty] = useState(false);
  useEffect(() => {
    const id = setInterval(() => {
      setIsDirty(!!(dirtyRef.current && dirtyRef.current()));
    }, 400);
    return () => clearInterval(id);
  }, []);
  // 화면 전환 시 즉시 초기화
  useEffect(() => { setIsDirty(false); }, [view]);

  // 저장 가드: 미저장 변경이 있는 채로 이동/로그아웃하려 할 때 보류된 동작
  const [pendingNav, setPendingNav] = useState(null);
  const [guardSaving, setGuardSaving] = useState(false);

  const openPwModal = () => { setPwForm({ current: '', next: '', confirm: '' }); setPwError(''); setPwModal(true); };
  const closePwModal = () => { if (pwSaving) return; setPwModal(false); };
  const submitPw = async (e) => {
    e.preventDefault();
    if (!pwForm.current) return setPwError('현재 비밀번호를 입력하세요.');
    if (!pwForm.next || pwForm.next.length < 4) return setPwError('새 비밀번호는 4자 이상이어야 합니다.');
    if (pwForm.next !== pwForm.confirm) return setPwError('새 비밀번호가 일치하지 않습니다.');
    setPwSaving(true); setPwError('');
    try {
      const res = await authFetch('/api/accounts/me/password', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.next }),
      });
      if (!res.ok) {
        let msg = `변경 실패 (${res.status})`;
        try { const b = await res.json(); if (b.message) msg = b.message; } catch (_) {}
        throw new Error(msg);
      }
      setPwModal(false);
      flashToast('비밀번호가 변경되었습니다.');
    } catch (err) {
      setPwError(err.message);
    } finally {
      setPwSaving(false);
    }
  };

  const flashToast = (msg) => {
    setToast(msg);
    window.clearTimeout(flashToast._t);
    flashToast._t = window.setTimeout(() => setToast(''), 2600);
  };

  const hasUnsaved = () => !!(dirtyRef.current && dirtyRef.current());
  // 이동 동작을 가드로 감쌈 — 변경사항이 있으면 확인 모달을 띄우고, 없으면 즉시 실행
  const requestNavigate = (action) => {
    if (hasUnsaved()) setPendingNav(() => action);
    else action();
  };

  const handleSave = async () => {
    if (saveRef.current) {
      setSaving(true);
      try {
        await saveRef.current();
        // 저장 성공 → 페이지 리마운트로 DB 최신값을 다시 읽어 표시
        // reloadKey 변경으로 site-preview가 리마운트되면 스크롤이 0으로 초기화되므로
        // 현재 스크롤 위치를 저장했다가 다음 프레임에서 복원한다.
        const savedScroll = mainContentRef.current?.scrollTop ?? 0;
        setReloadKey((k) => k + 1);
        requestAnimationFrame(() => {
          if (mainContentRef.current) mainContentRef.current.scrollTop = savedScroll;
        });
        flashToast('저장되었습니다. (DB 반영)');
      } catch (e) {
        flashToast(`저장 실패: ${e.message}`);
      } finally {
        setSaving(false);
      }
      return;
    }
    flashToast('이 페이지는 아직 저장 연동 전입니다. (UI 데모)');
  };

  // 가드 모달: 저장 후 이동 / 저장하지 않고 이동 / 취소(머무름)
  const guardSave = async () => {
    const action = pendingNav;
    if (!saveRef.current) { setPendingNav(null); if (action) action(); return; }
    setGuardSaving(true);
    try {
      await saveRef.current();
      flashToast('저장되었습니다. (DB 반영)');
      setPendingNav(null);
      if (action) action();
    } catch (e) {
      flashToast(`저장 실패: ${e.message}`); // 실패 시 이동하지 않고 머무름
      setPendingNav(null);
    } finally {
      setGuardSaving(false);
    }
  };
  const guardDiscard = () => { const action = pendingNav; setPendingNav(null); if (action) action(); };
  const guardCancel = () => setPendingNav(null);

  return (
    <div className="admin-shell">
      {/* 사이드바 */}
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <img src="/images/Header_Logo_Acetec.png" alt="AceTec" className="admin-brand-logo" />
          <span className="admin-brand-tag">Admin</span>
        </div>

        <nav className="admin-nav">
          <a href={HOMEPAGE_BASE} target="_blank" rel="noreferrer" className="admin-homepage-link">
            홈페이지 이동 ↗
          </a>
          {canManageAccounts && (
            <>
              <p className="admin-nav-label">계정 관리</p>
              <button
                type="button"
                className={`admin-nav-item${isAccounts ? ' is-active' : ''}`}
                onClick={() => requestNavigate(() => setView('accounts'))}
              >
                계정 관리
              </button>
              <button
                type="button"
                className={`admin-nav-item${isLogs ? ' is-active' : ''}`}
                onClick={() => requestNavigate(() => setView('logs'))}
              >
                활동 로그
              </button>
            </>
          )}

          <p className="admin-nav-label" style={canManageAccounts ? { marginTop: 18 } : undefined}>문의 접수</p>
          <button
            type="button"
            className={`admin-nav-item${isInquiries ? ' is-active' : ''}`}
            onClick={() => requestNavigate(() => setView('inquiries'))}
          >
            문의하기 내역
          </button>
          <button
            type="button"
            className={`admin-nav-item${isEduApps ? ' is-active' : ''}`}
            onClick={() => requestNavigate(() => setView('edu-apps'))}
          >
            교육 신청 내역
          </button>

          <p className="admin-nav-label" style={{ marginTop: 18 }}>페이지 관리</p>
          {PAGES.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`admin-nav-item${p.id === view ? ' is-active' : ''}`}
              onClick={() => requestNavigate(() => setView(p.id))}
            >
              {p.label}
            </button>
          ))}
        </nav>

        <div className="admin-user">
          <span className="admin-user-name">{account.charName || account.strID}</span>
          <span className="admin-user-role">{PERM_LABELS[account.intPermission] || `권한 ${account.intPermission}`}</span>
        </div>
        <button type="button" className="admin-pw-change" onClick={openPwModal}>
          비밀번호 변경
        </button>
        <button type="button" className="admin-logout" onClick={() => requestNavigate(() => onLogout())}>
          로그아웃
        </button>
      </aside>

      {/* 메인 */}
      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar-left">
            <span className="admin-topbar-title">
              {isAccounts
                ? '계정 관리'
                : isLogs
                ? '활동 로그'
                : isInquiries
                ? '문의하기 접수'
                : isEduApps
                ? '교육 신청 접수'
                : isProduct
                ? '제품 수정'
                : activePage?.label ?? ''}
            </span>
            <span className="admin-topbar-hint">
              {isAccounts
                ? '계정을 추가·수정·삭제합니다'
                : isLogs
                ? '관리자 로그인·작업 내역'
                : isInquiries
                ? '홈페이지 문의 폼 접수 내역'
                : isEduApps
                ? '홈페이지 교육 신청 접수 내역'
                : isProduct
                ? '응용 분야 제품 페이지를 편집합니다'
                : view === 'privacy'
                ? '홈페이지 폼에 표시되는 개인정보처리방침을 편집합니다'
                : '텍스트를 클릭해 바로 수정하세요'}
            </span>
          </div>
          {!isViewer && (
            <div className="admin-topbar-actions">
              {others.length > 0 && (
                <div className="admin-presence">
                  <span className="admin-presence-label">편집 중</span>
                  {others.map((name) => (
                    <span key={name} className="admin-presence-chip">{name}</span>
                  ))}
                </div>
              )}
              <button
                type="button"
                className={`admin-btn-save${isDirty ? ' is-dirty' : ''}`}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '저장 중…' : '저장'}
              </button>
            </div>
          )}
        </header>

        {isAccounts ? (
          <main className="admin-content">
            <AccountManager />
          </main>
        ) : isLogs ? (
          <main className="admin-content">
            <LogViewer />
          </main>
        ) : isInquiries ? (
          <main className="admin-content">
            <InquiryLogs />
          </main>
        ) : isEduApps ? (
          <main className="admin-content">
            <EduApplicationLogs />
          </main>
        ) : isProduct ? (
          <main ref={mainContentRef} className="admin-content admin-content--site">
            <div className="site-preview" key={view}>
              {(() => {
                const Editor = PROD_EDITORS[productSlug];
                return Editor ? (
                  <Editor refreshKey={reloadKey} onRegisterSave={registerSave} onRegisterDirty={registerDirty} onBack={() => requestNavigate(() => setView('solutions'))} />
                ) : (
                  <ProductCategory refreshKey={reloadKey} category={productSlug} onBack={() => requestNavigate(() => setView('solutions'))} onRegisterSave={registerSave} onRegisterDirty={registerDirty} />
                );
              })()}
            </div>
          </main>
        ) : (
          <main ref={mainContentRef} className="admin-content admin-content--site">
            <div className="site-preview" key={view}>
              <activePage.Comp refreshKey={reloadKey} onOpenProduct={openProduct} onRegisterSave={registerSave} onRegisterDirty={registerDirty} />
            </div>
          </main>
        )}
      </div>

      {pendingNav && (
        <div className="admin-guard-backdrop" onClick={guardSaving ? undefined : guardCancel}>
          <div className="admin-guard-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-guard-title">저장하지 않은 변경사항</h3>
            <p className="admin-guard-text">
              저장하지 않은 변경사항이 있습니다.<br />저장한 뒤 이동할까요?
            </p>
            <div className="admin-guard-actions">
              <button type="button" className="admin-btn-ghost" onClick={guardDiscard} disabled={guardSaving}>
                저장하지 않음
              </button>
              <button type="button" className="admin-btn-dark" onClick={guardSave} disabled={guardSaving}>
                {guardSaving ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pwModal && (
        <div className="admin-guard-backdrop" onClick={pwSaving ? undefined : closePwModal}>
          <div className="admin-guard-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-guard-title">비밀번호 변경</h3>
            <form onSubmit={submitPw} autoComplete="off">
              <div className="admin-pw-fg">
                <label>현재 비밀번호</label>
                <input type="password" autoComplete="current-password" value={pwForm.current}
                  onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} />
              </div>
              <div className="admin-pw-fg">
                <label>새 비밀번호</label>
                <input type="password" autoComplete="new-password" value={pwForm.next}
                  onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} />
              </div>
              <div className="admin-pw-fg">
                <label>새 비밀번호 확인</label>
                <input type="password" autoComplete="new-password" value={pwForm.confirm}
                  onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
              </div>
              {pwError && <p className="admin-pw-error">{pwError}</p>}
              <div className="admin-guard-actions" style={{ marginTop: 20 }}>
                <button type="button" className="admin-btn-ghost" onClick={closePwModal} disabled={pwSaving}>취소</button>
                <button type="submit" className="admin-btn-dark" disabled={pwSaving}>
                  {pwSaving ? '변경 중…' : '변경'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && <div className="admin-toast">{toast}</div>}
    </div>
  );
}
